import React, { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { Col, Row } from '@/lib/utils';
import { DemiLightText, RegularText } from '@/lib/Text';
import SvgIcon from '@/components/SvgIcon';
import NumberTextInput from '@/components/NumberTextInput';
import SelectMenu from '@/components/DropDown/SelectMenu';
import { colors } from '@/lib/colors';
import {
    ExceptionRateRule,
    ExceptionRatesFrom,
    InRateZeroableReason,
    ExceptionRuleDraft,
    ExceptionSaveFormat,
    ExceptionSettlementMode,
    buildRulesToSave,
    describeRetroactiveExceptionSave,
    exceptionModeOptions,
    formatExceptionRule,
    hasZeroValue,
    introducesInMultiplier,
    isSameRules,
    ruleTargetOf,
    splitRulesForMode,
    toDrafts,
    toRules,
    useSaveCoachExceptionRates,
    usesThreeValues,
    validateExceptionDrafts,
} from '@/features/coachCommissionTable';
import { SettlementCategory, getSettlementClassificationErrorMessage } from '@/features/settlementClassification';
import DialogShell from './DialogShell';
import OptionalValueInput from './OptionalValueInput';
import ExceptionRatesGuide, { useExceptionRatesGuide } from './ExceptionRatesGuide';
import { RemoveButton } from '../BracketPrimitives';
import ModalSettlementCategoryManage from '../../../ClassPass/SettlementCategory/ModalSettlementCategoryManage';
import { zeroableReasonLabel } from '@/features/noshowAlwaysIn/rules';
import { useTemplateCountsByCategory } from '@/features/settlementClassification/templateCounts';

type Props = {
    centerId: string;
    year: string;
    month: string;
    coachId: string;
    coachName: string;
    rules: ExceptionRateRule[];
    rulesFrom: ExceptionRatesFrom | null;
    categories: SettlementCategory[];
    /** 지금 센터의 정산 방식 — 입력 모양(기본 정산은 칸 셋)과 문구가 달라진다. 생략하면 상세 정산. */
    settlementMode?: ExceptionSettlementMode;
    /**
     * 저장 형식. 'legacy' = 이전 서버 — 요율 방식을 빼고 예전 형식으로 저장한다. 생략하면 형식 2.
     * 'v3' = 「시간 상관없이 IN 요율」을 아는 서버 — 상세 정산에서 그 방식을 고를 수 있다.
     */
    saveFormat?: ExceptionSaveFormat;
    /**
     * 기본 정산: 이 강사의 이 달 기본 정산 비율이 서버에 없는가. 서버는 기본 비율 문서가 없는 강사에게는 예외도 적용하지 않는다.
     * 화면은 「문서가 없음」과 「문서는 있는데 이 달 항목만 없음(이때 고정·요율은 적용)」을 가릴 수 없어 안내만 한다.
     */
    baseRateMissing?: boolean;
    /**
     * 상세 정산: 이 강사의 이 달 IN 구간표가 0원이 될 수 있는 이유(서버 inRateZeroable). 있으면 「항상 IN 요율의 배수」를
     * 처음 걸 때 한 번 더 확인받는다 — 근무 시간 밖 수업이 OUT 요율에서 0원으로 바뀔 수 있다.
     */
    inRateZeroable?: InRateZeroableReason | null;
    readOnly: boolean;
    onClose: () => void;
    onSaved: (coachId: string, rules: ExceptionRateRule[], from: ExceptionRatesFrom | null) => void;
    onCopy: () => void;
};

const isSameMonth = (from: ExceptionRatesFrom | null, year: string, month: string) =>
    !!from && Number(from.year) === Number(year) && Number(from.month) === Number(month);

const ExceptionRatesModal = ({
    centerId,
    year,
    month,
    coachId,
    coachName,
    rules,
    rulesFrom,
    categories,
    settlementMode = 'tier',
    saveFormat = 'v2',
    baseRateMissing = false,
    inRateZeroable = null,
    readOnly,
    onClose,
    onSaved,
    onCopy,
}: Props) => {
    const isBase = settlementMode === 'base';
    // 기본 정산은 「재등록 수강권 전체」 예외를 쓰지 않는다 — 숨기되 저장 때 그대로 되돌려 보낸다.
    const { visible: visibleRules, hidden: hiddenRules } = useMemo(
        () => splitRulesForMode(rules, settlementMode),
        [rules, settlementMode],
    );
    // 숨긴 규칙의 대상은 고를 수 없다(저장 배열에 함께 실려 같은 대상이 둘이 된다).
    const hiddenTargets = useMemo(() => hiddenRules.map(ruleTargetOf), [hiddenRules]);
    // 「시간 상관없이 IN 요율」 규칙은 상세 정산에서만 계산된다 — 다른 방식 화면에서는 회색으로 따로 보인다.
    const hiddenInMultiplierRules = hiddenRules.filter((rule) => rule.mode === 'inMultiplier');
    const hiddenOtherRules = hiddenRules.filter((rule) => rule.mode !== 'inMultiplier');
    // 처음 여는 사람에게 예외 수업료가 무엇인지 한 번 보여 준다(이후엔 「예외 수업료란?」으로).
    const guide = useExceptionRatesGuide();
    const { showOnce } = guide;
    useEffect(() => {
        showOnce();
    }, [showOnce]);
    const [drafts, setDrafts] = useState<ExceptionRuleDraft[]>(() => toDrafts(visibleRules));
    const [error, setError] = useState<string | null>(null);
    const [confirmZero, setConfirmZero] = useState(false);
    // IN 요율이 0일 수 있는 강사에게 「항상 IN 요율의 배수」를 처음 걸 때의 확인
    const [confirmInZero, setConfirmInZero] = useState(false);
    // 지난 달부터 적용하는 저장 확인 문구(있으면 확인 창을 띄운다)
    const [confirmRetro, setConfirmRetro] = useState<string | null>(null);
    const [isCategoryManageOpen, setIsCategoryManageOpen] = useState(false);
    const save = useSaveCoachExceptionRates();

    const isDirty = !isSameRules(drafts, visibleRules);
    const busy = save.isLoading;

    const categoryByKey = useMemo(() => new Map(categories.map((c) => [c.key, c])), [categories]);
    // 분류별 상품 수(상품 목록에서 센다). 못 읽으면 null — 개수 표시를 생략한다.
    const templateCounts = useTemplateCountsByCategory({ centerId, enabled: categories.length > 0 });
    const countLabel = (key: string) => (templateCounts ? ` · 상품 ${templateCounts.get(key) ?? 0}개` : '');
    const categoryNameOf = (key: string) => categoryByKey.get(key)?.name ?? '알 수 없는 분류';
    /** 편집 중인 행을 「재등록 수강권은 1회 30,000원」처럼 읽어 준다. 대상·값이 없으면 null. */
    const previewOf = (draft: ExceptionRuleDraft) =>
        draft.target && draft.value !== null && !Number.isNaN(draft.value)
            ? formatExceptionRule(toRules([draft])[0], categoryNameOf, settlementMode)
            : null;

    /** 대상 선택지: 보관 안 된 분류 + 지금 고른 보관/알 수 없는 분류 + 재등록 */
    const targetOptions = (current: string) => {
        const options: { value: string; label: string }[] = categories
            .filter((c) => !c.archived && !hiddenTargets.includes(`category:${c.key}`))
            .map((c) => ({ value: `category:${c.key}`, label: `${c.name} 상품${countLabel(c.key)}` }));
        if (current.startsWith('category:')) {
            const key = current.slice('category:'.length);
            const category = categoryByKey.get(key);
            if (!category) options.push({ value: current, label: '알 수 없는 분류' });
            else if (category.archived) options.push({ value: current, label: `${category.name} 상품 (보관됨)` });
        }
        if (!isBase && !hiddenTargets.includes('reRegistration')) {
            options.push({ value: 'reRegistration', label: '재등록 수강권' });
        }
        return options;
    };

    /**
     * 방식을 바꾸면 단위가 바뀌므로 값을 모두 비운다. 상세·세션별 정산에서 대상을 바꾸면 보이지 않는
     * 기본 정산 전용 값도 비운다(다른 분류에 몰래 따라가지 않게).
     */
    const changeTarget = (index: number, target: string) =>
        updateDraft(index, isBase ? { target } : { target, reRegistrationValue: null, noshowValue: null });
    // 「시간 상관없이 IN 요율」은 1(IN 요율 그대로)로 시작한다.
    const changeMode = (index: number, mode: ExceptionRuleDraft['mode']) =>
        updateDraft(index, {
            mode,
            value: mode === 'inMultiplier' ? 1 : null,
            reRegistrationValue: null,
            noshowValue: null,
        });

    const updateDraft = (index: number, patch: Partial<ExceptionRuleDraft>) => {
        setError(null);
        setConfirmZero(false);
        setConfirmInZero(false);
        setDrafts((prev) => prev.map((d, i) => (i === index ? { ...d, ...patch } : d)));
    };

    const addDraft = () => {
        setError(null);
        const used = new Set([...drafts.map((d) => d.target), ...hiddenTargets]);
        const first = [
            ...categories.filter((c) => !c.archived).map((c) => `category:${c.key}`),
            ...(isBase ? [] : ['reRegistration']),
        ].find((t) => !used.has(t));
        setDrafts((prev) => [
            ...prev,
            { target: first ?? '', mode: 'fixed', value: null, reRegistrationValue: null, noshowValue: null },
        ]);
    };

    const handleSave = async () => {
        if (readOnly || busy || !isDirty) return;
        const invalid = validateExceptionDrafts(drafts, settlementMode, hiddenTargets);
        if (invalid) {
            setError(invalid);
            return;
        }
        if (!confirmZero && hasZeroValue(drafts, settlementMode)) {
            setConfirmZero(true);
            return;
        }
        if (
            !confirmInZero &&
            settlementMode === 'tier' &&
            !!inRateZeroable &&
            introducesInMultiplier(drafts, visibleRules)
        ) {
            setConfirmInZero(true);
            return;
        }
        // 이번 달보다 이전 달에 저장하면 그 달~지난달 급여가 다시 계산된다 — 확인을 받고 저장한다.
        const now = dayjs();
        const retro = describeRetroactiveExceptionSave({ year, month }, { year: now.year(), month: now.month() + 1 });
        if (retro) {
            setConfirmRetro(retro);
            return;
        }
        await submit();
    };

    const submit = async () => {
        try {
            const toSave = buildRulesToSave(drafts, hiddenRules);
            const res = await save.mutateAsync({
                centerId,
                body: { coachId, year, month, exceptionRates: toSave },
                format: saveFormat,
            });
            onSaved(coachId, res?.exceptionRates ?? toSave, res?.exceptionRatesFrom ?? { year, month });
            onClose();
        } catch (e) {
            setConfirmZero(false);
            setConfirmInZero(false);
            setError(getSettlementClassificationErrorMessage(e as any));
        }
    };

    return (
        <DialogShell title={`${coachName} · 예외 수업료`} width={640} onClose={onClose} busy={busy}>
            <Col className="px-[24px] pb-[22px] overflow-y-auto">
                <RegularText fontSize={14} lineHeight={21} color="#6B7280" className="mt-[6px] whitespace-pre-line">
                    {`고른 수강권으로 한 수업은 ${isBase ? '정산 비율' : '구간 요율'} 대신 여기서 정한 대로 계산해요.\n${Number(year)}년 ${Number(
                        month,
                    )}월부터 적용되고, 다음 달도 따로 정하지 않으면 이어져요. 저장하면 바로 반영돼요.`}
                </RegularText>
                <button
                    type="button"
                    onClick={guide.show}
                    className="self-start mt-[6px] text-[13px] leading-[20px] text-primary underline underline-offset-4"
                >
                    예외 수업료란? 처음 쓰는 법 보기
                </button>
                {isBase && baseRateMissing && (
                    <div
                        role="note"
                        aria-label="기본 정산 비율 안내"
                        className="mt-[8px] rounded-[8px] bg-[#FFF6E5] px-[12px] py-[8px] text-[13px] leading-[19px] text-[#8A5A00] break-keep"
                    >
                        이 강사는 이 달 기본 정산 비율이 비어 있어요. 기본 정산 비율을 한 번도 적용하지 않은 강사라면 예외
                        수업료가 반영되지 않아요.
                    </div>
                )}
                {rulesFrom && !isSameMonth(rulesFrom, year, month) && (
                    <DemiLightText fontSize={13} color="#6B7280" className="mt-[4px]">
                        {`지금 값은 ${Number(rulesFrom.year)}년 ${Number(rulesFrom.month)}월에 정한 값이에요.`}
                    </DemiLightText>
                )}

                <Row className="mt-[16px] mb-[8px] gap-[10px]">
                    <Row className="w-[220px] items-center justify-between pr-[4px]">
                        <DemiLightText fontSize={13} color="#6B7280">
                            어떤 수강권
                        </DemiLightText>
                        {!readOnly && (
                            <RegularText
                                fontSize={13}
                                color={colors.primary}
                                className="cursor-pointer hover:underline"
                                onClick={() => setIsCategoryManageOpen(true)}
                            >
                                분류 관리
                            </RegularText>
                        )}
                    </Row>
                    <DemiLightText fontSize={13} color="#6B7280" className="w-[170px]">
                        계산 방식
                    </DemiLightText>
                    <DemiLightText fontSize={13} color="#6B7280">
                        값
                    </DemiLightText>
                </Row>

                {drafts.length === 0 && (
                    <Col className="py-[18px] items-center rounded-[10px] bg-[#F6F8FA] mb-[8px]">
                        <RegularText fontSize={14} color="#6B7280">
                            {visibleRules.length > 0 ? '저장하면 이 달부터 예외 없이 계산해요.' : '아직 예외가 없어요.'}
                        </RegularText>
                    </Col>
                )}

                <Col className="gap-[10px]">
                    {drafts.map((draft, index) => {
                        const threeValues = usesThreeValues(settlementMode, draft.mode);
                        const unit = draft.mode === 'fixed' ? '원' : draft.mode === 'percentage' ? '%' : '배';
                        const mainInput = (
                            <NumberTextInput
                                key={`${index}-${draft.mode}`}
                                value={draft.value ?? ''}
                                onChange={(value) => updateDraft(index, { value })}
                                unit={unit}
                                comma={draft.mode === 'fixed'}
                                allowDecimal={draft.mode !== 'fixed'}
                                placeholder={
                                    draft.mode === 'fixed' ? '금액 입력' : draft.mode === 'percentage' ? '예) 40' : '예) 1.2'
                                }
                                height={43}
                                disabled={readOnly}
                            />
                        );
                        return (
                        <Col key={index} className="gap-[4px]">
                        <Row className="items-center gap-[10px]">
                            {/* 기본 <select> 는 맥 크롬에서 위로 펼쳐져 위쪽 안내·머리글을 가려 아래로 열리는 목록으로 바꿨다 */}
                            <SelectMenu
                                aria-label="어떤 수강권"
                                className="w-[220px]"
                                value={draft.target}
                                placeholder="골라 주세요"
                                disabled={readOnly}
                                options={targetOptions(draft.target)}
                                onChange={(value) => changeTarget(index, value)}
                            />
                            <SelectMenu
                                aria-label="계산 방식"
                                className="w-[170px]"
                                value={draft.mode}
                                disabled={readOnly}
                                options={exceptionModeOptions(settlementMode, saveFormat, draft.mode)}
                                onChange={(value) => changeMode(index, value as ExceptionRuleDraft['mode'])}
                            />
                            <div className="w-[150px]">{!threeValues && mainInput}</div>
                            {!readOnly && (
                                <RemoveButton
                                    onClick={() => {
                                        setError(null);
                                        setConfirmZero(false);
                                        setConfirmInZero(false);
                                        setDrafts((prev) => prev.filter((_, i) => i !== index));
                                    }}
                                />
                            )}
                        </Row>
                        {threeValues && (
                            // 기본 정산의 고정 금액·요율 — 신규(필수) / 재등록(비우면 신규 값) / 노쇼(비우면 출석 값)
                            <Col className="gap-[6px] pl-[230px] mt-[2px]">
                                <Row className="items-center gap-[8px]">
                                    <DemiLightText fontSize={14} color="#4B5563" className="w-[48px]">
                                        신규
                                    </DemiLightText>
                                    <div className="w-[150px]">{mainInput}</div>
                                </Row>
                                <Row className="items-center gap-[8px]">
                                    <DemiLightText fontSize={14} color="#4B5563" className="w-[48px]">
                                        재등록
                                    </DemiLightText>
                                    <div className="w-[150px]">
                                        <OptionalValueInput
                                            key={`${index}-${draft.mode}-re`}
                                            ariaLabel="재등록 값"
                                            value={draft.reRegistrationValue}
                                            onChange={(value) => updateDraft(index, { reRegistrationValue: value })}
                                            unit={draft.mode === 'fixed' ? '원' : '%'}
                                            disabled={readOnly}
                                        />
                                    </div>
                                    <DemiLightText fontSize={13} color="#6B7280">
                                        비우면 신규 값
                                    </DemiLightText>
                                </Row>
                                <Row className="items-center gap-[8px]">
                                    <DemiLightText fontSize={14} color="#4B5563" className="w-[48px]">
                                        노쇼
                                    </DemiLightText>
                                    <div className="w-[150px]">
                                        <OptionalValueInput
                                            key={`${index}-${draft.mode}-ns`}
                                            ariaLabel="노쇼 값"
                                            value={draft.noshowValue}
                                            onChange={(value) => updateDraft(index, { noshowValue: value })}
                                            unit={draft.mode === 'fixed' ? '원' : '%'}
                                            disabled={readOnly}
                                        />
                                    </div>
                                    <DemiLightText fontSize={13} color="#6B7280">
                                        비우면 출석 값
                                    </DemiLightText>
                                </Row>
                            </Col>
                        )}
                        {draft.mode === 'inMultiplier' && (
                            <DemiLightText fontSize={13} lineHeight={18} color="#6B7280" className="pl-[4px]">
                                수업 시간과 상관없이 IN 구간 요율로 계산해요. 1이면 IN 요율 그대로예요.
                            </DemiLightText>
                        )}
                        {isBase && draft.mode === 'multiplier' && (
                            <DemiLightText fontSize={13} lineHeight={18} color="#6B7280" className="pl-[4px]">
                                신규·재등록·노쇼 수업 모두에 곱해요.
                            </DemiLightText>
                        )}
                        {previewOf(draft) && (
                            <DemiLightText fontSize={13} lineHeight={18} color="#6B7280" className="pl-[4px]">
                                {previewOf(draft)}
                            </DemiLightText>
                        )}
                        {templateCounts &&
                            draft.target.startsWith('category:') &&
                            (templateCounts.get(draft.target.slice('category:'.length)) ?? 0) === 0 && (
                                <DemiLightText fontSize={13} lineHeight={18} color="#B45309" className="pl-[4px]">
                                    이 분류가 붙은 상품이 없어요. 상품에서 이 분류를 지정해야 적용돼요.
                                </DemiLightText>
                            )}
                        </Col>
                        );
                    })}
                </Col>

                {!readOnly && (
                    <Row className="mt-[14px] items-center cursor-pointer gap-[8px] self-start" onClick={addDraft}>
                        <SvgIcon name="SvgPlus" size={14} fill={colors.primary} />
                        <RegularText fontSize={15} color={colors.primary}>
                            추가하기
                        </RegularText>
                    </Row>
                )}

                <span
                    className="mt-[16px] self-start"
                    title={
                        isBase
                            ? '· 수강권에 따로 정한 요율이 있으면 그 요율이 먼저예요.\n· 요율(%)은 1회 판매가에 곱해요. 부가세 설정도 그대로 따라요.\n· 배수는 정산 비율로 계산한 수업료에 곱하고, 원 단위는 버려요.\n· 재등록 수강권 전체 예외는 기본 정산에서는 쓰지 않아요.'
                            : `· 수강권에 따로 정한 요율이 있으면 그 요율이 먼저예요.\n· 분류 예외와 재등록 예외가 둘 다 맞으면 분류 예외를 써요.\n· 요율(%)은 1회 판매가에 곱해요. 부가세 설정도 그대로 따라요.\n· 배수는 구간 요율로 계산한 수업료에 곱하고, 원 단위는 버려요.${
                                  settlementMode === 'tier' && saveFormat === 'v3'
                                      ? '\n· 항상 IN 요율의 배수는 수업 시간과 상관없이 IN 구간 수업료에 곱해요.'
                                      : ''
                              }`
                    }
                >
                    <DemiLightText fontSize={13} lineHeight={18} color="#6B7280">
                        수강권에 따로 정한 요율이 있으면 그 요율이 먼저예요. ⓘ
                    </DemiLightText>
                </span>

                {hiddenOtherRules.length > 0 && (
                    <DemiLightText fontSize={13} lineHeight={18} color="#6B7280" className="mt-[6px]">
                        재등록 수강권 예외는 기본 정산에서 쓰지 않아 여기서는 보이지 않아요. 저장해도 지워지지 않아요.
                    </DemiLightText>
                )}
                {hiddenInMultiplierRules.length > 0 && (
                    <Col
                        aria-label="상세 정산에서만 계산되는 예외"
                        className="mt-[8px] rounded-[8px] bg-[#F6F8FA] px-[12px] py-[8px]"
                    >
                        {hiddenInMultiplierRules.map((rule) => (
                            <DemiLightText key={ruleTargetOf(rule)} fontSize={13} lineHeight={19} color="#9CA3AF">
                                {`· ${formatExceptionRule(rule, categoryNameOf, 'tier')}`}
                            </DemiLightText>
                        ))}
                        <DemiLightText fontSize={13} lineHeight={19} color="#6B7280" className="mt-[2px]">
                            상세 정산에서만 계산돼요. 저장해도 지워지지 않아요.
                        </DemiLightText>
                    </Col>
                )}
                {error && (
                    <RegularText fontSize={14} color="#D93043" className="mt-[12px]">
                        {error}
                    </RegularText>
                )}
                {confirmInZero && !error && (
                    <RegularText fontSize={14} lineHeight={21} color="#B45309" className="mt-[12px] break-keep">
                        {`이 강사는 매출 구간에 따라 IN 요율이 0일 수 있어요(${zeroableReasonLabel(
                            inRateZeroable ?? undefined,
                        )}). 그 구간에서는 근무 시간 밖 수업도 IN 요율로 계산돼 0원이 돼요. 맞으면 한 번 더 저장을 눌러 주세요.`}
                    </RegularText>
                )}
                {confirmZero && !error && (
                    <RegularText fontSize={14} color="#B45309" className="mt-[12px]">
                        0원(0%)인 칸이 있어요. 그 수업은 수업료가 0원이 돼요. 맞으면 한 번 더 저장을 눌러 주세요.
                    </RegularText>
                )}

                {!readOnly && (
                    <Row className="items-center justify-between mt-[20px]">
                        <Col>
                            <RegularText
                                fontSize={14}
                                color={isDirty || rules.length === 0 ? '#6B7280' : colors.primary}
                                className={isDirty || rules.length === 0 ? 'cursor-not-allowed' : 'cursor-pointer underline underline-offset-4'}
                                onClick={isDirty || rules.length === 0 || busy ? undefined : onCopy}
                            >
                                다른 강사에게 같은 예외 적용
                            </RegularText>
                            {isDirty && rules.length > 0 && (
                                <DemiLightText fontSize={13} color="#6B7280" className="mt-[2px]">
                                    먼저 저장해 주세요
                                </DemiLightText>
                            )}
                        </Col>
                        <Row className="gap-[8px]">
                            <div
                                className="h-[40px] w-[88px] rounded-[9px] bg-[#F4F5F7] flex items-center justify-center cursor-pointer"
                                onClick={busy ? undefined : onClose}
                            >
                                <RegularText fontSize={15} color="#3D4244">
                                    취소
                                </RegularText>
                            </div>
                            <div
                                className={`h-[40px] w-[96px] rounded-[9px] bg-primary flex items-center justify-center ${
                                    !isDirty || busy ? 'bg-opacity-30 cursor-not-allowed' : 'cursor-pointer'
                                }`}
                                onClick={handleSave}
                            >
                                <RegularText fontSize={15} color="white">
                                    {busy ? '저장 중…' : '저장'}
                                </RegularText>
                            </div>
                        </Row>
                    </Row>
                )}
            </Col>
            {confirmRetro && (
                <DialogShell
                    title="지난 달 급여도 바뀌어요"
                    width={420}
                    onClose={() => !busy && setConfirmRetro(null)}
                    busy={busy}
                >
                    <Col className="px-[24px] pb-[22px]">
                        <RegularText fontSize={15} lineHeight={23} color="#3D4244" className="mt-[10px] break-keep">
                            {confirmRetro}
                        </RegularText>
                        <DemiLightText fontSize={13} lineHeight={20} color="#6B7280" className="mt-[6px] break-keep">
                            그 사이 따로 정한 달이 있으면 그 달부터는 그 달 값을 써요.
                        </DemiLightText>
                        <Row className="justify-end gap-[8px] mt-[20px]">
                            <div
                                className="h-[40px] w-[88px] rounded-[9px] bg-[#F4F5F7] flex items-center justify-center cursor-pointer"
                                onClick={busy ? undefined : () => setConfirmRetro(null)}
                            >
                                <RegularText fontSize={15} color="#3D4244">
                                    취소
                                </RegularText>
                            </div>
                            <div
                                className={`h-[40px] w-[96px] rounded-[9px] bg-primary flex items-center justify-center ${
                                    busy ? 'bg-opacity-30 cursor-not-allowed' : 'cursor-pointer'
                                }`}
                                onClick={
                                    busy
                                        ? undefined
                                        : async () => {
                                              await submit();
                                              setConfirmRetro(null);
                                          }
                                }
                            >
                                <RegularText fontSize={15} color="white">
                                    {busy ? '저장 중…' : '저장'}
                                </RegularText>
                            </div>
                        </Row>
                    </Col>
                </DialogShell>
            )}
            {isCategoryManageOpen && (
                <ModalSettlementCategoryManage
                    centerId={centerId}
                    categories={categories}
                    onClose={() => setIsCategoryManageOpen(false)}
                />
            )}
            {guide.open && <ExceptionRatesGuide onClose={guide.close} settlementMode={settlementMode} />}
        </DialogShell>
    );
};

export default ExceptionRatesModal;
